/**
 * Helpers for the user-insights embed page.
 *
 * The embed route param (`/embed/user-insights/:userId`) is overloaded: it is
 * either a plain numeric user ID or an `adc:user:phone-{E.164}` URN. These
 * pure functions encapsulate parsing that param so the container stays focused
 * on rendering.
 */

import Urn from '@adc/structures/urn'

/** Sub-prefix carried in a URN's id slot that marks a phone-number lookup. */
export const PHONE_ID_PREFIX = 'phone-'

/**
 * Extracts a prefixed sub-identifier from a URN's id slot.
 *
 * e.g. `extractPrefixedId('adc:user:phone-+15551234567', 'phone-')` -> `'+15551234567'`.
 *
 * Returns `null` when the value is not a URN (fewer than 3 `:`-segments, e.g.
 * a bare user ID) or when its id slot does not carry the given prefix.
 */
export function extractPrefixedId(urn: string, prefix: string): string | null {
  const id = Urn.safelyParse(urn)?.identifier
  if (!id) return null
  return id.startsWith(prefix) ? id.slice(prefix.length) : null
}

/**
 * Normalizes a phone number to the US national form the graph `parties` filter
 * matches against. The platform stores `number` without the country code, so a
 * leading `+1` / `1` must be dropped or the lookup finds nothing.
 *
 * Strips all non-digits, then drops a leading US country code `1` when the
 * result is 11 digits. e.g. `'+1 (201) 204-7358'` -> `'2012047358'`.
 *
 * US-only (`+1`) by design — the embed is a US Amazon Connect flow.
 */
export function toNationalPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '')
  return digits.length === 11 && digits.startsWith('1')
    ? digits.slice(1)
    : digits
}

/**
 * Extracts the verified-phone number from an `adc:user:phone-{E.164}` URN,
 * normalized to the US national form for the `parties` phone filter.
 * Returns `null` for anything that is not a phone URN (e.g. a plain user ID).
 */
export function extractPhoneFromUserUrn(ref: string): string | null {
  const raw = extractPrefixedId(ref, PHONE_ID_PREFIX)
  return raw === null ? null : toNationalPhone(raw)
}
